import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { getStorage, ref, deleteObject } from 'firebase/storage';

/**
 * Récupère les réunions d'un utilisateur depuis Firestore
 * @param {string} userId - ID de l'utilisateur
 * @param {number} limitCount - Nombre maximum de réunions à récupérer
 * @returns {Promise<Array>} - Liste des réunions
 */
export const getMeetings = async (userId, limitCount = 50) => {
  try {
    
    // Vérifier si l'ID a le préfixe 'azure-'
    if (userId && !userId.startsWith('azure-') && localStorage.getItem('azureTestUser')) {

      userId = `azure-${userId}`;
    }
    
    const meetingsQuery = query(
      collection(db, 'meetings'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(meetingsQuery);

    if (querySnapshot.docs.length === 0) {
      
      // Tenter une autre requête sans filtrer par utilisateur (pour déboguer)
      const allMeetingsQuery = query(
        collection(db, 'meetings'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const allQuerySnapshot = await getDocs(allMeetingsQuery);
      
      if (allQuerySnapshot.docs.length > 0) {
        allQuerySnapshot.docs.forEach((doc, index) => {
        });
      }
    }
    
    const meetingsList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      date: doc.data().date || doc.data().createdAt?.toDate().toISOString().split('T')[0]
    }));
    
    meetingsList.forEach((meeting, index) => {
    });
    
    return meetingsList;
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère toutes les réunions depuis Firestore sans filtre d'utilisateur
 * @param {number} limitCount - Nombre maximum de réunions à récupérer
 * @returns {Promise<Array>} - Liste de toutes les réunions
 */
export const getAllMeetings = async (limitCount = 100) => {
  try {
    
    // Requête sans filtre de userId pour récupérer toutes les réunions
    const allMeetingsQuery = query(
      collection(db, 'meetings'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(allMeetingsQuery);

    const meetingsList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      date: doc.data().date || doc.data().createdAt?.toDate().toISOString().split('T')[0]
    }));
    
    meetingsList.slice(0, 5).forEach((meeting, index) => {
    });
    
    return meetingsList;
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère une réunion spécifique depuis Firestore
 * @param {string} meetingId - ID de la réunion
 * @returns {Promise<Object>} - Données de la réunion
 */
export const getMeeting = async (meetingId) => {
  try {
    const meetingDoc = await getDoc(doc(db, 'meetings', meetingId));
    
    if (!meetingDoc.exists()) {
      throw new Error('Réunion non trouvée');
    }
    
    const data = meetingDoc.data();
    return {
      id: meetingDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      date: data.date || data.createdAt?.toDate().toISOString().split('T')[0]
    };
  } catch (error) {

    throw error;
  }
};

/**
 * Crée une nouvelle réunion dans Firestore
 * @param {Object} meetingData - Données de la réunion
 * @returns {Promise<string>} - ID de la réunion créée
 */
export const createMeeting = async (meetingData) => {
  try {
    const meetingWithTimestamp = {
      ...meetingData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'meetings'), meetingWithTimestamp);
    return docRef.id;
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère les transcriptions d'un utilisateur depuis Firestore
 * @param {string} userId - ID de l'utilisateur
 * @param {number} limitCount - Nombre maximum de transcriptions à récupérer
 * @returns {Promise<Array>} - Liste des transcriptions
 */
export const getTranscriptions = async (userId, limitCount = 10) => {
  try {
    const transcriptionsQuery = query(
      collection(db, 'transcriptions'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(transcriptionsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    }));
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère une transcription spécifique depuis Firestore
 * @param {string} transcriptionId - ID de la transcription
 * @returns {Promise<Object>} - Données de la transcription
 */
export const getTranscription = async (transcriptionId) => {
  try {
    const transcriptionDoc = await getDoc(doc(db, 'transcriptions', transcriptionId));
    
    if (!transcriptionDoc.exists()) {
      throw new Error('Transcription non trouvée');
    }
    
    const data = transcriptionDoc.data();
    return {
      id: transcriptionDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date()
    };
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère les résumés d'un utilisateur depuis Firestore
 * @param {string} userId - ID de l'utilisateur
 * @param {number} limitCount - Nombre maximum de résumés à récupérer
 * @returns {Promise<Array>} - Liste des résumés
 */
export const getSummaries = async (userId, limitCount = 10) => {
  try {
    const summariesQuery = query(
      collection(db, 'summaries'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(summariesQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    }));
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère un résumé spécifique depuis Firestore
 * @param {string} summaryId - ID du résumé
 * @returns {Promise<Object>} - Données du résumé
 */
export const getSummary = async (summaryId) => {
  try {
    const summaryDoc = await getDoc(doc(db, 'summaries', summaryId));
    
    if (!summaryDoc.exists()) {
      throw new Error('Résumé non trouvé');
    }
    
    const data = summaryDoc.data();
    return {
      id: summaryDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date()
    };
  } catch (error) {

    throw error;
  }
};

/**
 * Supprime une réunion de Firestore
 * @param {string} meetingId - ID de la réunion à supprimer
 * @returns {Promise<void>}
 */
export const deleteMeeting = async (meetingId) => {
  try {
    // 1. Récupérer les données de la réunion pour obtenir le chemin du fichier audio
    const meetingDoc = await getDoc(doc(db, 'meetings', meetingId));
    
    if (meetingDoc.exists()) {
      const meetingData = meetingDoc.data();
      
      // 2. Vérifier si la réunion a un fichier audio associé
      if (meetingData.audioUrl) {
        try {
          // Initialiser le storage
          const storage = getStorage();
          
          // Extraire le chemin du fichier à partir de l'URL
          // L'URL complète ressemble à: https://firebasestorage.googleapis.com/v0/b/[bucket]/o/[chemin_encodé]?token...
          // Vérifier que l'URL contient '/o/' avant de faire le split
          if (meetingData.audioUrl.includes('/o/')) {
            const audioPath = decodeURIComponent(meetingData.audioUrl.split('/o/')[1].split('?')[0]);
            
            // Créer une référence au fichier
            const audioRef = ref(storage, audioPath);
            
            // Supprimer le fichier audio
            await deleteObject(audioRef);
          } else {

          }
        } catch (error) {

          // Continuer malgré l'erreur pour supprimer la réunion
        }
      }
    }
    
    // 3. Supprimer le document de la réunion
    await deleteDoc(doc(db, 'meetings', meetingId));
  } catch (error) {

    throw error;
  }
};

/**
 * Supprime une transcription de Firestore
 * @param {string} transcriptionId - ID de la transcription à supprimer
 * @returns {Promise<void>}
 */
export const deleteTranscription = async (transcriptionId) => {
  try {
    await deleteDoc(doc(db, 'transcriptions', transcriptionId));
  } catch (error) {

    throw error;
  }
};

/**
 * Supprime un résumé de Firestore
 * @param {string} summaryId - ID du résumé à supprimer
 * @returns {Promise<void>}
 */
export const deleteSummary = async (summaryId) => {
  try {
    await deleteDoc(doc(db, 'summaries', summaryId));
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère les utilisateurs depuis Firestore pour l'autocomplétion
 * @param {string} searchTerm - Terme de recherche pour filtrer les utilisateurs
 * @param {number} limitCount - Nombre maximum d'utilisateurs à récupérer
 * @returns {Promise<Array>} - Liste des utilisateurs
 */
export const getUsers = async (searchTerm = '', limitCount = 10) => {
  try {
    // Convertir le terme de recherche en minuscules pour une recherche insensible à la casse
    const searchTermLower = searchTerm.toLowerCase();
    
    // Sans terme de recherche, récupérer tous les utilisateurs (limité à limitCount)
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('displayName'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(usersQuery);
    let users = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Si un terme de recherche est fourni, filtrer côté client
    // Note: Firestore ne supporte pas directement les recherches par sous-chaîne
    if (searchTermLower) {
      users = users.filter(user => 
        user.displayName?.toLowerCase().includes(searchTermLower) ||
        user.email?.toLowerCase().includes(searchTermLower) ||
        user.firstName?.toLowerCase().includes(searchTermLower) ||
        user.lastName?.toLowerCase().includes(searchTermLower)
      );
    }
    
    // Formater les résultats pour l'affichage
    return users.map(user => ({
      id: user.id,
      displayName: user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      email: user.email,
      photoURL: user.photoURL,
      firstName: user.firstName,
      lastName: user.lastName,
      title: user.title || user.jobTitle || '' // Poste/titre professionnel
    }));
  } catch (error) {

    return [];
  }
};

/**
 * Récupère les projets d'un utilisateur depuis Firestore
 * @param {string} userId - ID de l'utilisateur
 * @param {number} limitCount - Nombre maximum de projets à récupérer
 * @returns {Promise<Array>} - Liste des projets
 */
export const getProjects = async (userId, limitCount = 50) => {
  try {
    const projectsQuery = query(
      collection(db, 'projects'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(projectsQuery);

    const projects = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    });

    return projects;
  } catch (error) {
    if (error.code === 'failed-precondition' && error.message.includes('index')) {
      console.warn('[FirestoreUtils] Index en cours de construction, utilisation du fallback');

      try {
        const fallbackQuery = query(
          collection(db, 'projects'),
          where('userId', '==', userId),
          limit(limitCount)
        );

        const querySnapshot = await getDocs(fallbackQuery);

        const projects = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          };
        });

        projects.sort((a, b) => b.createdAt - a.createdAt);

        return projects;
      } catch (fallbackError) {
        console.error('[FirestoreUtils] Erreur fallback:', fallbackError.message);
        throw fallbackError;
      }
    }

    console.error('[FirestoreUtils] Erreur getProjects:', error.message);
    throw error;
  }
};

/**
 * Récupère un projet spécifique depuis Firestore
 * @param {string} projectId - ID du projet
 * @returns {Promise<Object>} - Données du projet
 */
export const getProject = async (projectId) => {
  try {
    const projectDoc = await getDoc(doc(db, 'projects', projectId));

    if (!projectDoc.exists()) {
      throw new Error('Projet non trouvé');
    }

    const data = projectDoc.data();
    return {
      id: projectDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  } catch (error) {

    throw error;
  }
};

/**
 * Crée un nouveau projet dans Firestore
 * @param {Object} projectData - Données du projet
 * @returns {Promise<string>} - ID du projet créé
 */
export const createProject = async (projectData) => {
  try {
    const projectWithTimestamp = {
      ...projectData,
      meetingIds: projectData.meetingIds || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'projects'), projectWithTimestamp);
    return docRef.id;
  } catch (error) {

    throw error;
  }
};

/**
 * Met à jour un projet dans Firestore
 * @param {string} projectId - ID du projet
 * @param {Object} projectData - Nouvelles données du projet
 * @returns {Promise<void>}
 */
export const updateProject = async (projectId, projectData) => {
  try {
    const projectRef = doc(db, 'projects', projectId);
    const updatedData = {
      ...projectData,
      updatedAt: serverTimestamp()
    };

    await updateDoc(projectRef, updatedData);
  } catch (error) {

    throw error;
  }
};

/**
 * Supprime un projet de Firestore
 * @param {string} projectId - ID du projet à supprimer
 * @returns {Promise<void>}
 */
export const deleteProject = async (projectId) => {
  try {
    await deleteDoc(doc(db, 'projects', projectId));
  } catch (error) {

    throw error;
  }
};

/**
 * Ajoute une réunion à un projet
 * @param {string} projectId - ID du projet
 * @param {string} meetingId - ID de la réunion à ajouter
 * @returns {Promise<void>}
 */
export const addMeetingToProject = async (projectId, meetingId) => {
  try {
    const projectDoc = await getDoc(doc(db, 'projects', projectId));

    if (!projectDoc.exists()) {
      throw new Error('Projet non trouvé');
    }

    const currentMeetingIds = projectDoc.data().meetingIds || [];

    if (!currentMeetingIds.includes(meetingId)) {
      await updateDoc(doc(db, 'projects', projectId), {
        meetingIds: [...currentMeetingIds, meetingId],
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {

    throw error;
  }
};

/**
 * Retire une réunion d'un projet
 * @param {string} projectId - ID du projet
 * @param {string} meetingId - ID de la réunion à retirer
 * @returns {Promise<void>}
 */
export const removeMeetingFromProject = async (projectId, meetingId) => {
  try {
    const projectDoc = await getDoc(doc(db, 'projects', projectId));

    if (!projectDoc.exists()) {
      throw new Error('Projet non trouvé');
    }

    const currentMeetingIds = projectDoc.data().meetingIds || [];
    const updatedMeetingIds = currentMeetingIds.filter(id => id !== meetingId);

    await updateDoc(doc(db, 'projects', projectId), {
      meetingIds: updatedMeetingIds,
      updatedAt: serverTimestamp()
    });
  } catch (error) {

    throw error;
  }
};
